import { PlatformUserDomainRecord } from '../../../../models/domain/platform-user-domain-record'
import { RankedDomain, OrganizationGroup, ListItemType } from '../types'
import { SubjectEntity } from '../../../../enums/ability-entity.enum'
import { AbilityAction } from '../../../../enums/ability-action.enum'
import { ICaslSubject, asCaslSubject } from '../../../../stores/app-ability'

/**
 * Minimal typed interface for the abilityStore parameter.
 * Matches the AbilityStore.can() signature.
 */
export interface AbilityStoreSlice {
    can?<T extends SubjectEntity>(action: AbilityAction, subject: T | ICaslSubject<T>): boolean
}

/**
 * Build flat list items for the All tab
 *
 * Constructs a flat array of list items from filtered domains, including:
 * - Recents section (when query is empty and recents exist)
 * - Organization headers (when showOrganizations=true)
 * - Domain items
 * - Empty state when no results
 *
 * @param filteredDomains - Array of ranked domains from search/filter
 * @param orgMap - Map of accountId to OrganizationGroup
 * @param recents - Array of recent domain ids
 * @param activeDomainId - Currently active domain id
 * @param showOrganizations - Whether to show organization headers
 * @param query - Current search query
 * @param abilityStore - Ability store for CASL access checks
 * @param domainMap - Map of domainId to PlatformUserDomainRecord
 * @returns Flat array of list items for virtualization
 */
export const buildListItems = (
    filteredDomains: RankedDomain[],
    orgMap: Map<number, OrganizationGroup>,
    recents: number[],
    activeDomainId: number | undefined,
    showOrganizations: boolean,
    query: string,
    abilityStore: AbilityStoreSlice,
    domainMap: Map<number, PlatformUserDomainRecord>,
): ListItemType[] => {
    const items: ListItemType[] = []
    const trimmedQuery = query.trim()

    // Track which domain ids are in recents to avoid duplication
    const recentsSet = new Set(recents)

    // Add recents section when query is empty
    if (!trimmedQuery && recents.length > 0) {
        items.push({ type: 'recents-header' })

        // Add recent domain items
        for (const domainId of recents) {
            const domain = domainMap.get(domainId)
            if (domain) {
                items.push({
                    type: 'domain',
                    domain,
                    inRecents: true,
                })
            }
        }
    }

    // Handle empty results
    if (filteredDomains.length === 0) {
        items.push({ type: 'empty' })
        return items
    }

    // Group filtered domains by organization
    const orgDomains = new Map<number, PlatformUserDomainRecord[]>()

    for (const { domain } of filteredDomains) {
        if (!orgDomains.has(domain.accountId)) {
            orgDomains.set(domain.accountId, [])
        }
        orgDomains.get(domain.accountId)!.push(domain)
    }

    // Build list items with organization grouping
    if (showOrganizations) {
        // Get sorted organization list
        const sortedOrgs: OrganizationGroup[] = []
        for (const [accountId, domains] of orgDomains) {
            const org = orgMap.get(accountId)
            if (org) {
                sortedOrgs.push({
                    accountId,
                    accountName: org.accountName,
                    domains,
                })
            }
        }

        // Sort organizations alphabetically
        sortedOrgs.sort((a, b) => a.accountName.toLowerCase().localeCompare(b.accountName.toLowerCase()))

        // Add organization headers and domain items
        for (const org of sortedOrgs) {
            // Determine org access via CASL abilities (server-side computed).
            //
            // The org subject must include `statusId: 1`. Server-side serialized rules
            // carry the raw policy conditions: the external account READ policy is scoped
            // to `{ id, statusId: 1 }`, while the internal policy is only scoped to `{ id }`.
            // CASL matches a rule's conditions against the subject's fields, so omitting
            // statusId causes the external rule to never match — silently hiding org
            // navigation from external users. Active status is guaranteed here because the
            // public platform only ever surfaces active orgs to the user (matches the
            // convention in user-access-well.tsx and halocline's app.service).
            const canAccess = abilityStore?.can
                ? abilityStore.can(
                      AbilityAction.READ,
                      asCaslSubject(SubjectEntity.ORG, { id: org.accountId, statusId: 1 }),
                  )
                : true

            // Add organization header
            items.push({
                type: 'org-header',
                accountId: org.accountId,
                accountName: org.accountName,
                canAccess,
            })

            // Add domain items for this organization
            for (const domain of org.domains) {
                items.push({
                    type: 'domain',
                    domain,
                    inRecents: false,
                })
            }
        }
    } else {
        // Flat list mode: no organization headers
        const allDomains: PlatformUserDomainRecord[] = []

        for (const domains of orgDomains.values()) {
            allDomains.push(...domains)
        }

        // Sort domains alphabetically by displayName
        allDomains.sort((a, b) => a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase()))

        // Add domain items
        for (const domain of allDomains) {
            items.push({
                type: 'domain',
                domain,
                inRecents: false,
            })
        }
    }

    return items
}

/**
 * Build flat list items for the Pinned tab
 *
 * Constructs a flat alphabetical list of pinned domains without
 * organization grouping. Shows empty state when no pinned domains exist.
 *
 * @param filteredDomains - Array of ranked domains (filtered to pinned only)
 * @param activeDomainId - Currently active domain id
 * @returns Flat array of list items for virtualization
 *
 * Requirements: 6a.1, 6a.12, 6a.14
 */
export const buildPinnedListItems = (
    filteredDomains: RankedDomain[],
    activeDomainId: number | undefined,
): ListItemType[] => {
    const items: ListItemType[] = []

    if (filteredDomains.length === 0) {
        items.push({ type: 'empty' })
        return items
    }

    // Flat alphabetical list - no org grouping for pinned tab
    const sortedDomains = [...filteredDomains].sort((a, b) =>
        a.domain.displayName.toLowerCase().localeCompare(b.domain.displayName.toLowerCase()),
    )

    for (const { domain } of sortedDomains) {
        items.push({
            type: 'domain',
            domain,
            inRecents: false,
        })
    }

    return items
}
